import { Component, OnInit, inject, DestroyRef, ViewChild, ChangeDetectorRef, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { interval, Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { translate } from '@jsverse/transloco';
import { DatePipe, UpperCasePipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { CustomToastrService } from 'src/app/services/util/toastr.service';
import { SidenavService } from 'src/app/services/util/sidenav.service';
import { BaseImageService } from 'src/app/services/api/base-image.service';
import { InstancesService } from 'src/app/services/api/instances.service';
import { ColumnFilterComponent } from 'src/app/main/instances-mgmt/column-filter/column-filter.component';
import { ConfirmationDialogComponent } from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import { EnvironmentTableComponent } from '../environment-table/environment-table.component';
import { EnvironmentPaginatorComponent } from '../environment-paginator/environment-paginator.component';
import { BaseImageDetailPopupComponent } from './base-image-detail-popup/base-image-detail-popup.component';
import { Constants } from 'src/app/models/constants';
import { AvailabilityStatus } from 'src/app/models/availability-status';
import { DeployStatus } from 'src/app/models/deploy/deploy-status';

@Component({
  selector: 'app-environment-base-image',
  templateUrl: './environment-base-image.component.html',
  styleUrl: './environment-base-image.component.scss',
  standalone: false
})
export class EnvironmentBaseImageComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  @ViewChild(EnvironmentPaginatorComponent) paginatorRef: EnvironmentPaginatorComponent;
  @ViewChild(EnvironmentTableComponent) tableRef: EnvironmentTableComponent;

  @Input() instance: any;
  instanceId: string;
  instanceName = '';
  instanceCreatedAt: Date;
  AvailabilityStatus = AvailabilityStatus;
  readonly BASE_IMAGE_ENABLE_STATES = [
    DeployStatus.DEPLOYED,
    DeployStatus.UPDATED,
    DeployStatus.UPDATE_FAILED,
    DeployStatus.CONFIGURED,
    DeployStatus.CONFIGURATION_FAILED,
    DeployStatus.TESTED,
    DeployStatus.TEST_FAILED,
  ];

  get isBaseImageEnabled(): boolean {
    return this.BASE_IMAGE_ENABLE_STATES.includes(this.instance?.status);
  }

  showLoader = true;
  isRefreshing = false;
  baseImageResponse = false;
  isUpdatePending = false;
  isRollbackPending = false;
  inProgressBaseImage: any = null;
  baseImageUpdatedAt: string | null = null;
  remainingTimeDisplay = '';
  private timerSub: Subscription | null = null;
  baseImageData: any[] = [];
  allBaseImageData: any[] = [];
  filterStartDate: any;
  filterEndDate: any;
  searchTerm = '';
  searchedResult: any[] = [];
  pageIndex = 0;
  paginationSize = 10;
  Constants = Constants;


  displayColumns = [
    {
      key: 'name',
      name: 'INSTANCE_DETAILS.NAME',
      type: 'text',
      sortable: true,
      visible: true,
      disable: true
    },
    {
      key: 'createdBy',
      name: 'INSTANCE_DETAILS.UPDATED_BY_DB',
      type: 'user',
      sortable: true,
      visible: true,
      disable: true
    },
    {
      key: 'createdAt',
      name: 'INSTANCE_DETAILS.UPDATED_ON',
      type: 'date',
      sortable: true,
      visible: true,
      disable: false
    },
    {
      key: 'status',
      name: 'INSTANCE_DETAILS.STATUS',
      type: 'status',
      sortable: true,
      visible: true,
      disable: true
    },
    {
      key: 'baseImageSettings',
      name: '',
      type: 'settings',
      visible: true,
      disable: true
    }
  ];

  constructor(
    private activatedRoute: ActivatedRoute,
    private baseImageService: BaseImageService,
    private instancesService: InstancesService,
    private toastrService: CustomToastrService,
    private sidenavService: SidenavService,
    private datePipe: DatePipe,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog
  ) {}

  /** Reads instanceId from route and triggers initial data load. */
  ngOnInit(): void {
    this.instanceId = this.activatedRoute.snapshot.params.instanceId;
    this.loadInstanceDetails();
    this.loadBaseImages();
  }

  /** Fetches instance details to get the creation date used in date filters. */
  private loadInstanceDetails(): void {
    this.instancesService.getInstanceDetails(this.instanceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (instance) => {
          this.instanceCreatedAt = instance?.dateCreated;
          this.instanceName = instance?.name || '';
        },
        error: (error) => {
          setTimeout(() => {
            const title = translate('ERRORS.SERVICE_AVAILABILITY');
            const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
            this.toastrService.errMessage(message, title);
          }, 500);
        }
      });
  }

  /** Fetches base image update records for the current instance, re-fetching the full set when it exceeds one page. */
  loadBaseImages(): void {
    this.showLoader = true;
    this.baseImageService.getBaseImageUpdates(this.instanceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (Number(response?.total) > this.paginationSize) {
            this.baseImageService.getBaseImageUpdates(this.instanceId, response?.total)
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe({
                next: (allResponse) => this.processBaseImages(allResponse),
                error: (error) => this.handleBaseImageError(error)
              });
          } else {
            this.processBaseImages(response);
          }
        },
        error: (error) => this.handleBaseImageError(error)
      });
  }

  /** Populates base image list state and loads the latest update to drive the countdown timer. */
  private processBaseImages(response: any): void {
    this.allBaseImageData = response?.data || [];
    this.baseImageData = [...this.allBaseImageData];
    this.inProgressBaseImage = this.baseImageData.find(item => item.status === 'inprogress') || null;
    this.baseImageResponse = this.baseImageData.length > 0;
    this.baseImageService.getCurrentBaseImageUpdate(this.instanceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (latestResponse) => {
          const latestStatus = latestResponse?.data?.status || latestResponse?.status;
          this.baseImageUpdatedAt = latestStatus !== 'failed'
            ? (latestResponse?.data?.updatedAt || latestResponse?.updatedAt || null)
            : null;
          this.startRemainingTimer();
          this.showLoader = false;
        },
        error: () => {
          this.startRemainingTimer();
          this.showLoader = false;
        }
      });
  }

  /** Shows a toast for base image load failures. */
  private handleBaseImageError(error: any): void {
    this.showLoader = false;
    setTimeout(() => {
      const title = translate('ERRORS.SERVICE_AVAILABILITY');
      const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
      this.toastrService.errMessage(message, title);
    }, 500);
  }

  /** Reloads base images and resets paginator to first page. */
  refreshClick(): void {
    this.isRefreshing = true;
    if (this.paginatorRef) {
      this.paginatorRef.pageIndex = 0;
    }
    this.loadBaseImages();
    setTimeout(() => {
      this.isRefreshing = false;
    }, 1000);
  }

  /** Syncs paginator page change to the table component. */
  onPageChange(event: any): void {
    this.paginationSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.tableRef?.pageChanged(event);
  }

  /** Updates local pagination state when the table emits a page change. */
  onTablePageChanged(event: any): void {
    this.pageIndex = event.pageIndex;
    this.paginationSize = event.pageSize;
  }

  /** Resets the paginator back to the first page. */
  resetPaginator(): void {
    if (this.paginatorRef) {
      this.paginatorRef.pageIndex = 0;
    }
  }

  /** Updates the active search term used to filter table rows. */
  search(searchTerm?: string): void {
    this.searchTerm = searchTerm;
  }

  /** Receives filtered results from the table and triggers change detection. */
  searchedItems(result: any): void {
    this.searchedResult = result;
    this.cdr.detectChanges();
  }

  /** Opens the date filter sidenav and applies the selected date range to the table. */
  openFilter(): void {
    const data = {
      name: 'BaseImage',
      title: translate('INSTANCE_STATUS.FILTER_TITLE'),
      subtitle: translate('INSTANCE_STATUS.FILTER_SUBTITLE'),
      filteredColumns: ['date'],
      dateCreated: this.instanceCreatedAt,
      startDate: this.filterStartDate,
      endDate: this.filterEndDate,
      filterOptions: [{ value: translate('BASE_IMAGE.UPDATED_DATE_FILTER'), selected: !!(this.filterStartDate && this.filterEndDate) }]
    };
    this.sidenavService.openInRightSidenav(ColumnFilterComponent, data)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (!result) { return; }
        const dateFilter = result[0];
        if (dateFilter?.filteredStartDate && dateFilter?.filteredEndDate) {
          this.filterStartDate = dateFilter.filteredStartDate;
          this.filterEndDate = dateFilter.filteredEndDate;
          this.baseImageData = this.allBaseImageData.filter(item =>
            this.datePipe.transform(item.createdAt, 'yyyy-MM-dd') >= this.datePipe.transform(this.filterStartDate, 'yyyy-MM-dd') &&
            this.datePipe.transform(item.createdAt, 'yyyy-MM-dd') <= this.datePipe.transform(this.filterEndDate, 'yyyy-MM-dd')
          );
        } else {
          this.filterStartDate = undefined;
          this.filterEndDate = undefined;
          this.baseImageData = [...this.allBaseImageData];
        }
        this.baseImageResponse = this.baseImageData.length > 0;
        this.resetPaginator();
        this.cdr.detectChanges();
      });
  }

  /** Clears the active date filter and restores the full base image list. */
  cancelFilter(): void {
    this.filterStartDate = undefined;
    this.filterEndDate = undefined;
    this.baseImageData = [...this.allBaseImageData];
    this.baseImageResponse = this.baseImageData.length > 0;
    this.resetPaginator();
  }

  /** Opens a confirmation dialog and calls createBaseImageUpdate on confirm, then reloads the list. */
  onUpdateClick(): void {
    this.openBaseImageUpdateDialog();
  }

  /** Opens a confirmation dialog for retrying a failed base image update and calls createBaseImageUpdate on confirm. */
  onRetryClick(_row: any): void {
    this.openBaseImageUpdateDialog(true);
  }

  private openBaseImageUpdateDialog(isRetry?: boolean): void {
    this.isUpdatePending = true;
    this.dialog.open(ConfirmationDialogComponent, {
      panelClass: 'default-dialog-wrapper',
      width: '566px',
      autoFocus: false,
      data: {
        title: isRetry ? translate('INSTANCE_DETAILS.UPDATE_BUILD.RETRY_BASE_IMG_TITLE', { instanceName: this.instanceName }) : translate('INSTANCE_DETAILS.UPDATE_BUILD.BASE_IMG_TITLE', { instanceName: this.instanceName }),
        message: isRetry ? translate('INSTANCE_DETAILS.UPDATE_BUILD.RETRY_BASE_IMG_MESSAGE') : translate('INSTANCE_DETAILS.UPDATE_BUILD.BASE_IMG_MESSAGE'),
        note: isRetry ? "" : translate('INSTANCE_DETAILS.UPDATE_BUILD.BASE_IMG_NOTE'),
        confirmButtonText: translate('DIALOG.CONFIRM'),
        page: 'baseImage'
      }
    }).afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirm => {
        if (!confirm) {
          this.isUpdatePending = false;
          return;
        }
        this.baseImageService.createBaseImageUpdate(this.instanceId, {})
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.loadBaseImages();
              this.isUpdatePending = false;
            },
            error: (error) => {
              this.isUpdatePending = false;
              setTimeout(() => {
                const title = translate('ERRORS.SERVICE_AVAILABILITY');
                const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
                this.toastrService.errMessage(message, title);
              }, 500);
            }
          });
      });
  }

  onNameClick(row: any): void {
    const dialogRef = this.dialog.open(BaseImageDetailPopupComponent, {
      panelClass: 'default-dialog-wrapper',
      width: '1200px',
      height: '80vh',
      autoFocus: false,
      data: { row, detail: [], loading: true, errors: [] }
    });

    this.baseImageService.getBaseImageUpdateById(this.instanceId, row.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          dialogRef.componentInstance.data.detail = (response?.components || []).map((comp: any) => ({
            componentName: comp.componentName,
            fromRef: comp.images?.[0]?.from?.ref?.split(':').pop() || '-',
            toRef: comp.images?.[0]?.to?.ref?.split(':').pop() || '-',
            status: comp.status,
            errors: comp.errors?.length ? comp.errors : (response?.errors || [])

          }));
          dialogRef.componentInstance.data.loading = false;
        },
        error: (error) => {
          dialogRef.componentInstance.data.loading = false;
          const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message || error?.error?.message || error?.errmsg || translate('SERVICE.INTERNAL_SERVER_ERROR');
          this.toastrService.errMessage(message, title);
        }
      });
  }

  /** Opens the rollback confirmation dialog for the selected base image */
  onRollbackClick(): void {
    this.openBaseImageRollbackDialog(true);
  }

  /** Starts a 1-second interval that counts down from inProgressBaseImage.updatedAt + 30 min and updates remainingTimeDisplay. */
  private startRemainingTimer(): void {
    this.timerSub?.unsubscribe();
    this.timerSub = null;
    if (!this.baseImageUpdatedAt) {
      this.remainingTimeDisplay = '';
      return;
    }
    const expiryTime = new Date(this.baseImageUpdatedAt).getTime() + 30 * 60 * 1000;
    const tick = () => {
      const remaining = expiryTime - Date.now();
      this.remainingTimeDisplay = remaining > 0 ? this.formatDuration(remaining) : '00:00:00';
      this.cdr.detectChanges();
    };
    tick();
    this.timerSub = interval(1000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(tick);
  }

  /** Converts a duration in milliseconds to HH:MM:SS format where hours can exceed 24. */
  private formatDuration(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  /** Upon confirmation, triggers the base image rollback API */
  private openBaseImageRollbackDialog(isRollback?: boolean): void {
    this.isRollbackPending = true;
    this.baseImageService.getCurrentBaseImageUpdate(this.instanceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (currentBaseImage) => {
          const imageName = currentBaseImage?.data?.name || currentBaseImage?.name || '';
          const updatedAt = currentBaseImage?.data?.updatedAt || currentBaseImage?.updatedAt || null;
          const updatedOn = this.datePipe.transform(updatedAt, 'MMM d, yyyy, HH:mm:ss', 'UTC') || '';

          this.dialog.open(ConfirmationDialogComponent, {
            panelClass: 'default-dialog-wrapper',
            width: '630px',
            autoFocus: false,
            data: {
              title: isRollback ? translate('INSTANCE_DETAILS.UPDATE_BUILD.ROLLBACK_BASE_IMG_TITLE', { instanceName: this.instanceName.toUpperCase() }) : translate('INSTANCE_DETAILS.UPDATE_BUILD.BASE_IMG_TITLE', { instanceName: this.instanceName.toUpperCase() }),
              message: isRollback ? translate('INSTANCE_DETAILS.UPDATE_BUILD.ROLLBACK_BASE_IMG_MESSAGE') : translate('INSTANCE_DETAILS.UPDATE_BUILD.BASE_IMG_MESSAGE'),
              note: isRollback ? "" : translate('INSTANCE_DETAILS.UPDATE_BUILD.BASE_IMG_NOTE'),
              confirmButtonText: translate('DIALOG.CONFIRM'),
              page: isRollback ? 'baseImageRollback' : 'baseImage',
              imageName,
              updatedOn
            }
          }).afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(confirm => {
              if (!confirm) {
                this.isRollbackPending = false;
                return;
              }
              const baseImageUpdateId = currentBaseImage?.data?.id || currentBaseImage?.id;
              this.baseImageService.rollbackBaseImageUpdate(this.instanceId, baseImageUpdateId)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                  next: () => {
                    this.loadBaseImages();
                    this.isRollbackPending = false;
                  },
                  error: (error) => {
                    this.isRollbackPending = false;
                    setTimeout(() => {
                      const title = translate('ERRORS.SERVICE_AVAILABILITY');
                      const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
                      this.toastrService.errMessage(message, title);
                    }, 500);
                  }
                });
            });
        },
        error: (error) => {
          this.isRollbackPending = false;
          setTimeout(() => {
            const title = translate('ERRORS.SERVICE_AVAILABILITY');
            const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
            this.toastrService.errMessage(message, title);
          }, 500);
        }
      });
  }
}
